import * as React from 'react';
import { ThemeProvider, extendTheme } from '@mui/joy/styles';
import Stepper from '@mui/joy/Stepper';
import Step, { stepClasses } from '@mui/joy/Step';
import StepIndicator from '@mui/joy/StepIndicator';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Typography } from '@mui/material';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';

const theme = extendTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    neutral: {
      plainDisabledColor: '#f0f0f0',
    },
    error: {
      main: '#d32f2f',
    },
  },
});

export default function ProgressStepper({ status = ['completed', 'inProgress', 'skipped', 'notStarted'] }) {
  const [hoverIndex, setHoverIndex] = React.useState(null);

  const stepData = [
    { label: 'Initiate' },
    { label: 'Action' },
    { label: 'Review' },
    { label: 'Closure' },
  ];

  const getStepStyles = (stepStatus) => {
    switch (stepStatus) {
      case 'completed':
        return { color: 'rgba(0, 164, 19, 1)', borderColor: 'rgba(0, 164, 19, 1)', lineColor: 'rgba(0, 164, 19, 1)' };
      case 'inProgress':
        return { color: 'rgba(42, 61, 189, 1)', borderColor: 'rgba(42, 61, 189, 1)', lineColor: 'rgba(42, 61, 189, 1)'};
      case 'skipped':
        return { color: 'orange', borderColor: 'orange', lineColor: 'orange' };
      case 'notStarted':
      default:
        return { color: 'gray', borderColor: 'gray', lineColor: 'rgba(217, 217, 217, 1)' };
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Stepper
        sx={{
          width: '300px',
          '--Step-connectorThickness': '2px',
          [`& .${stepClasses.root}`]: {
            flexDirection: 'column-reverse',
            '&::after': {
              top: 'unset',
              bottom: 'calc(var(--StepIndicator-size) / 2 - var(--Step-connectorThickness) / 2)',
            },
          }
        }}
      >
        {stepData.map((step, index) => {
          const stepStatus = status[index] || 'notStarted';
          const { color, borderColor, lineColor } = getStepStyles(stepStatus);

          return (
            <Step
              key={index}
              completed={stepStatus === 'completed'}
              sx={{
                '&::after': {
                  content: '""',
                  width: '100%',
                  height: '2px',
                  display: 'block',
                  backgroundImage: stepStatus === 'inProgress'
                    ? 'linear-gradient(to right, rgba(42, 61, 189, 1) 60%, rgba(217, 217, 217, 1) 50%)'
                    : 'none',
                  backgroundColor: stepStatus !== 'inProgress' ? lineColor : 'transparent',
                },
              }}
              onMouseOver={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
              orientation="vertical"
              indicator={
                <StepIndicator
                  variant="outlined"
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${borderColor}`,
                    backgroundColor: stepStatus === 'completed' ? color : stepStatus === 'notStarted' ? 'rgba(235, 235, 235, 1)' : 'white',
                    color: stepStatus === 'completed' ? 'white' : color,
                  }}
                >
                  {stepStatus === 'completed' ? (
                    <CheckRoundedIcon sx={{ fontSize: '16px' }} />
                  ) : stepStatus === 'inProgress' ? (
                    <CircleRoundedIcon sx={{ fontSize: '14px' }} />
                  ) : stepStatus === 'skipped' ? (
                    <SkipNextRoundedIcon sx={{ fontSize: '20px' }} />
                  ) : (
                    null
                  )}
                </StepIndicator>
              }
            >
              <Typography
                className='stepper-label'
                sx={{
                  position: 'absolute',
                  top: '30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  border: '1px solid rgba(211, 211, 211, 1)',
                  borderRadius: '5px',
                  py: '4px',
                  px: '6px',
                  background: 'white',
                  whiteSpace: 'nowrap',
                  visibility: hoverIndex === index ? 'visible' : 'hidden',
                  color: stepStatus === 'completed' ? (
                    'rgba(0, 0, 0, 1)'
                  ) : stepStatus === 'inProgress' ? (
                    'rgba(42, 61, 189, 1)'
                  ) : stepStatus === 'skipped' ? (
                    'orange'
                  ) : (
                    'rgba(84, 84, 84, 1)'
                  )
                }}
              >
                {step.label}
              </Typography>
            </Step>
          );
        })}
      </Stepper>
    </ThemeProvider>
  );
}
